<template>

<div id="app">

	<div class="app-body">
		<router-view :key="$route.fullPath" />
	</div>

	<!-- the one sign-in dialog, hosted at the root so any screen's Topbar can
	     open it via the bus; guests can dismiss and keep playing -->
	<auth-sign-in v-if="signinOpen" v-on:close="signinOpen = false" />

	<!-- a single transient error toast: any code can raise it with
	     bus.$emit('error', message); it auto-hides and is dismissable -->
	<div v-if="errorMessage" class="app-toast" role="alert" v-on:click="dismissError">
		<span class="app-toast-text">{{ errorMessage }}</span>
		<button type="button" class="app-toast-close" aria-label="Dismiss" v-on:click.stop="dismissError">&times;</button>
	</div>

</div>

</template>

<script>

import bus from './service/bus'
import SignIn from './components/auth/SignIn.vue'

export default {

	name: 'App',

	components: {
		'auth-sign-in': SignIn
	},

	data: function() {

		return {
			// whether the sign-in dialog is open (opened from any Topbar via bus)
			signinOpen: false,
			// the current transient error toast message (empty = hidden)
			errorMessage: '',
			// handle for the auto-hide timer
			errorTimer: null
		}

	},

	created: function() {

		var self = this

		this.openSignin = function() { self.signinOpen = true }
		this.onError = function(message) { self.showError(message) }

		bus.$on('signin', this.openSignin)
		bus.$on('error', this.onError)

		// recognise the stored session once on boot so a signed-in player isn't
		// shown as logged out (Topbar / Stats / admin curate all read this); we
		// don't block render on it — the UI updates when it resolves
		this.$store.dispatch('account/recognise')

	},

	beforeDestroy: function() {

		bus.$off('signin', this.openSignin)
		bus.$off('error', this.onError)

		if (this.errorTimer) clearTimeout(this.errorTimer)

	},

	methods: {

		showError: function(message) {

			this.errorMessage = message || 'Something went wrong.'

			if (this.errorTimer) clearTimeout(this.errorTimer)

			var self = this

			this.errorTimer = setTimeout(function() { self.errorMessage = '' }, 4000)

		},

		dismissError: function() {

			this.errorMessage = ''

			if (this.errorTimer) {

				clearTimeout(this.errorTimer)
				this.errorTimer = null

			}

		}

	}

}

</script>

<style>

#app {
	min-height: 100vh; /* fallback */
	min-height: calc(var(--vh, 1vh) * 100);
	background: var(--paper);
	color: var(--ink);
	font-family: var(--font-body);
	display: flex;
	flex-direction: column;
}

/*
	The chrome lives outside the router so only the content between it ever
	transitions. Screens fill the body.
*/
.app-body {
	flex: 1;
	position: relative;
	display: flex;
	flex-direction: column;
	padding-bottom: 20px;
}

.app-body > * {
	flex: 1;
}

/*
	The global error toast: a small paper card pinned bottom-centre with a
	--wrong accent border. Raised via bus.$emit('error', message), auto-hides.
*/
.app-toast {
	position: fixed;
	left: 50%;
	bottom: 22px;
	transform: translateX(-50%);
	z-index: 200;
	max-width: calc(100vw - 36px);
	display: flex;
	align-items: center;
	gap: 12px;
	background: var(--paper);
	color: var(--ink);
	border: 1px solid var(--wrong);
	border-left-width: 3px;
	border-radius: 10px;
	padding: 12px 14px;
	box-shadow: 0 16px 44px rgba(40, 34, 24, 0.2);
	font-family: var(--font-body);
	font-size: 14px;
	cursor: pointer;
	animation: app-toast-in 0.25s ease both;
}

@keyframes app-toast-in {
	from { transform: translate(-50%, 12px); opacity: 0; }
	to { transform: translate(-50%, 0); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
	.app-toast { animation: none; }
}

.app-toast-text {
	line-height: 1.4;
}

.app-toast-close {
	flex: none;
	background: none;
	border: none;
	color: var(--muted-2);
	font-size: 18px;
	line-height: 1;
	cursor: pointer;
	padding: 0 2px;
}

</style>
